import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  FileText, 
  Globe, 
  Sparkles, 
  ExternalLink, 
  CheckCircle2, 
  Search, 
  Layers, 
  Settings, 
  Copy, 
  Check, 
  Plus, 
  Edit3, 
  Eye, 
  Share2, 
  Code,
  ShieldCheck,
  Bot,
  Image as ImageIcon,
  Upload,
  Trash2,
  X,
  DollarSign,
  ArrowRight
} from 'lucide-react';


interface PagesCMSManagerProps {
  onSelectTab?: (tabId: string) => void;
  onOpenSeoPage?: (slug: string) => void;
}

export const PagesCMSManager: React.FC<PagesCMSManagerProps> = ({ onSelectTab }) => {
  const [activeTab, setActiveTab] = useState<'content' | 'media' | 'config' | 'schema'>('content');
  const [copied, setCopied] = useState(false);

  // Collections based on .pages.yml
  const collections = [
    {
      id: 'global_seo',
      name: 'Global Site SEO & Meta',
      type: 'file',
      path: 'src/content/settings/seo.json',
      icon: <Globe className="w-4 h-4" />,
      description: 'Site-wide SEO titles, descriptions, and OpenGraph settings.'
    }
  ];

  interface MediaItem {
    id: string;
    name: string;
    url: string;
    size?: string;
    dateAdded: string;
  }

  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const [mediaItems, setMediaItems] = useState<MediaItem[]>(() => {
    try {
      const saved = localStorage.getItem('randon_brown_pages_cms_media_v3');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // ignore
    }
    return [
      { id: "1", name: "portrait.png", url: "/uploads/portrait.png", size: "1.2 MB", dateAdded: "2026-10-27" },
      { id: "2", name: "Randon_Brown_Head.png", url: "/uploads/Randon_Brown_Head.png", size: "850 KB", dateAdded: "2026-10-27" },
      { id: "3", name: "a_boy_and_his_brother.png", url: "/uploads/a_boy_and_his_brother.png", size: "2.4 MB", dateAdded: "2026-10-27" },
      { id: "4", name: "favicon.svg", url: "/favicon.svg", size: "12 KB", dateAdded: "2026-10-27" }
    ];
  });

  useEffect(() => {
    try {
      localStorage.setItem('randon_brown_pages_cms_media_v3', JSON.stringify(mediaItems));
    } catch (e) {
      // ignore
    }
  }, [mediaItems]);

  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const result = uploadEvent.target?.result as string;
      if (result) {
        const newItem: MediaItem = {
          id: Date.now().toString(),
          name: file.name,
          url: result,
          size: `${(file.size / 1024).toFixed(1)} KB`,
          dateAdded: new Date().toISOString().split('T')[0]
        };
        setMediaItems(prev => [newItem, ...prev]);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteMedia = (id: string) => {
    setMediaItems(prev => prev.filter(m => m.id !== id));
  };

  const pagesYmlSample = `# Pages CMS Configuration (https://pagescms.org)
# Configured for Randon Brown, LLC - IT Leadership & Web Platform

media:
  input: public/uploads
  output: /uploads

# Modern Pages CMS content hierarchy
content:
  # 1. Global Site SEO & Metadata Settings (Single File Editor)
  - name: global_seo
    label: Global Site SEO & Meta
    type: file
    path: src/content/settings/seo.json
    format: json
    fields:
      - name: siteTitle
        label: Global Site Title
        type: string
        required: true
      - name: metaDescription
        label: Default Meta Description
        type: text
        required: true
      ... (fields truncated for brevity)`;

  return (
    <div className="max-w-6xl mx-auto space-y-8 py-4">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-neutral-900 via-brand-950/40 to-neutral-900 border border-brand-500/30 p-6 sm:p-8 shadow-2xl">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/15 border border-brand-500/30 text-brand-300 text-xs font-mono font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-brand-400" />
              <span>Pages CMS Integration Active</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-black text-white tracking-tight">
              Pages CMS Content Manager
            </h1>
            <p className="text-sm text-slate-300 max-w-2xl leading-relaxed">
              Manage your dynamic SEO landing pages, service offerings, and media powered by Pages CMS.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="https://pagescms.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 rounded-xl bg-brand-500 text-slate-950 font-bold text-xs hover:bg-brand-400 transition-all shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <span>Launch External Editor</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-neutral-800 pb-2 overflow-x-auto custom-scrollbar">
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'content'
              ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40 shadow-md'
              : 'text-slate-400 hover:text-slate-200 hover:bg-neutral-900'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Collections ({collections.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('media')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'media'
              ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40 shadow-md'
              : 'text-slate-400 hover:text-slate-200 hover:bg-neutral-900'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
          <span>Media Library ({mediaItems.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'config'
              ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40 shadow-md'
              : 'text-slate-400 hover:text-slate-200 hover:bg-neutral-900'
          }`}
        >
          <Settings className="w-4 h-4" />
          <span>.pages.yml Config</span>
        </button>
      </div>

      {/* TAB: COLLECTIONS */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {collections.map(col => (
              <div key={col.id} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 space-y-4 hover:border-brand-500/30 transition-all group">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 rounded-xl bg-brand-500/10 text-brand-400 group-hover:scale-110 transition-transform">
                    {col.icon}
                  </div>
                  <span className="text-[10px] font-mono text-slate-500 bg-black/40 px-2 py-0.5 rounded border border-white/5">
                    {col.type.toUpperCase()}
                  </span>
                </div>
                
                <div>
                  <h3 className="text-sm font-bold text-white group-hover:text-brand-300 transition-colors">{col.name}</h3>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    {col.description}
                  </p>
                </div>

                <div className="pt-2 flex items-center justify-between">
                  <code className="text-[10px] text-slate-500 font-mono">{col.path}</code>
                  <button className="text-[11px] font-bold text-brand-400 hover:text-brand-300 flex items-center gap-1 cursor-pointer">
                    <span>Manage</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Notice */}
          <div className="p-5 rounded-2xl bg-brand-500/5 border border-brand-500/20 flex gap-4 items-start">
            <Bot className="w-5 h-5 text-brand-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-sm font-bold text-white">Using Pages CMS</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                This internal manager provides a read-only view of your repository structure. To edit content, use the <strong>Launch External Editor</strong> button above to connect your GitHub repository to PagesCMS.org. Once connected, your SEO landing pages and service offerings will be fully editable through their friendly UI.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB: MEDIA LIBRARY */}
      {activeTab === 'media' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-brand-400" />
                <span>Pages CMS Media Library ({mediaItems.length} assets)</span>
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Manage your site images, hero banners, and social share graphics. Upload new images or copy paths for use in your project.
              </p>
            </div>

            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2.5 rounded-xl bg-brand-500 text-slate-950 font-bold text-xs hover:bg-brand-400 transition-all cursor-pointer shadow-md flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                <span>Upload New Image</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {mediaItems.map(item => (
              <div key={item.id} className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden shadow-lg flex flex-col justify-between group">
                <div className="h-48 bg-neutral-950 relative flex items-center justify-center overflow-hidden border-b border-neutral-800">
                  {imageErrors[item.id] ? (
                    <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-neutral-950 flex flex-col items-center justify-center p-4 text-center">
                      <ImageIcon className="w-10 h-10 text-brand-500/60 mb-2" />
                      <span className="text-xs font-mono text-slate-300 font-bold">{item.name}</span>
                      <span className="text-[10px] font-mono text-slate-500 mt-1">Ready for Use</span>
                    </div>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={() => {
                        setImageErrors(prev => ({ ...prev, [item.id]: true }));
                      }}
                    />
                  )}
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/70 backdrop-blur-md text-[10px] font-mono text-slate-300 border border-white/10">
                    {item.size || "Asset"}
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <div>
                    <h4 className="text-xs font-bold text-white truncate font-mono" title={item.name}>
                      {item.name}
                    </h4>
                    <p className="text-[11px] text-slate-400 font-mono truncate mt-0.5">
                      {item.url}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-neutral-800">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(item.url);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-xs text-slate-200 font-mono font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <Copy className="w-3.5 h-3.5 text-brand-400" />
                      <span>{copied ? 'Copied!' : 'Copy Path'}</span>
                    </button>

                    {item.id !== "1" && item.id !== "2" && item.id !== "3" && item.id !== "4" && (
                      <button
                        onClick={() => handleDeleteMedia(item.id)}
                        className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition-all cursor-pointer border border-rose-500/30"
                        title="Delete image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: CONFIG */}
      {activeTab === 'config' && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 space-y-4">
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand-400" />
              <span>Root Config File: <code className="text-brand-300 font-mono">.pages.yml</code></span>
            </h3>
            <p className="text-xs text-slate-400">
              Pages CMS reads this root configuration file to generate friendly editing interfaces for your repository.
            </p>
          </div>

          <pre className="p-4 rounded-xl bg-neutral-950 border border-neutral-800 font-mono text-[10px] sm:text-xs text-slate-300 overflow-x-auto leading-relaxed">
            {pagesYmlSample}
          </pre>
        </div>
      )}
    </div>
  );
};
